import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { apiFetch } from "@/lib/http";

async function throwIfResNotOk(res: Response) {
	if (!res.ok) {
		let errorData;
		try {
			// Try to parse JSON error response
			const text = await res.text();
			errorData = JSON.parse(text);
		} catch {
			// Fall back to status text if JSON parsing fails
			errorData = { message: res.statusText };
		}
		
		const detail = errorData.error || errorData.message || res.statusText;
		const reason = errorData.reason ? ` (${errorData.reason})` : '';
		const error = new Error(detail ? `${res.status}: ${detail}${reason}` : String(res.status));
		(error as any).status = res.status;
		(error as any).data = errorData;
		throw error;
	}
}

export async function apiRequest(
	url: string,
	method: string,
	data?: unknown | undefined,
): Promise<Response> {
	// Check if offline (only for mutations, not GET requests)
	const isMutation = method !== 'GET' && method !== 'HEAD';
	
	if (isMutation && typeof navigator !== 'undefined' && !navigator.onLine) {
		// Import offline queue functions
		const { addMutation } = await import('@/lib/offlineQueue');
		
		// Extract headers if needed (for auth token preservation)
		const headers: Record<string, string> = {};
		try {
			const { getAccessToken } = await import('@/lib/authBootstrap');
			const token = await getAccessToken();
			if (token) {
				headers['Authorization'] = `Bearer ${token}`;
			}
		} catch (error) {
			// Ignore auth errors during offline queuing
		}
		
		// Queue the mutation
		const mutationId = await addMutation(method, url, data, headers);
		console.log('[OfflineMutation] Queued mutation for offline sync:', mutationId, method, url);
		
		// Return a fake success response that mimics the expected structure
		// Include queued flag so callers can detect it
		return new Response(
			JSON.stringify({
				queued: true,
				message: 'Task saved offline – will sync automatically.',
				mutationId,
				success: true, // Some endpoints expect this
			}),
			{
				status: 200,
				statusText: 'OK',
				headers: { 'Content-Type': 'application/json' },
			}
		);
	}
	
	// Online: proceed normally
	// Detect if data is FormData (for file uploads)
	const isFormData = data instanceof FormData;
	
	const res = await apiFetch(url.startsWith('/') ? url : `/${url}`, {
		method,
		// Don't set Content-Type for FormData - browser will set it with boundary
		headers: data && !isFormData ? { "Content-Type": "application/json" } : {},
		// Send FormData as-is, stringify everything else
		body: data ? (isFormData ? data : JSON.stringify(data)) : undefined,
	});

	await throwIfResNotOk(res);
	return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn = <T>(options: {
	on401: UnauthorizedBehavior;
}): QueryFunction<T> => {
	const { on401: unauthorizedBehavior } = options;
	return async ({ queryKey }) => {
		const url = queryKey[0] as string;
		console.log(`[AUTH_DEBUG] Global queryFn fetching: ${url} from ${window.location.origin}${url}`);
		
		const res = await apiFetch(url.startsWith('/') ? url : `/${url}`);

		if (unauthorizedBehavior === "returnNull" && res.status === 401) {
			console.log(`[AUTH_DEBUG] 401 response for ${url}, returning null`);
			return null as unknown as T;
		}

		await throwIfResNotOk(res);
		const json = await res.json();
		
		// Debug logging for key endpoints
		if (url === '/api/campaigns') {
			console.log('[AUTH_DEBUG] Campaigns fetched successfully:', json?.length, 'campaigns');
		} else if (url === '/api/users/me') {
			console.log('[AUTH_DEBUG] /api/users/me response', json);
		}
		
		// Shim: unwrap { user } for /api/users/me so callers can keep expecting the user object
		if (url === "/api/users/me" && json && typeof json === 'object' && 'user' in json) {
			return (json.user as T);
		}
		return json as T;
	};
};

// Re-export the canonical queryClient from main.tsx
// This maintains backward compatibility for existing imports
export { queryClient } from '@/main';
